<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { camelize } from 'humps';
import { useQuery } from '@tanstack/vue-query';
import {
  shiftChangeRequestsApi,
  type ShiftChangeReasonCode,
  type ShiftChangeRequest,
  type ShiftChangeRequestFilters,
} from '@/api/shift-change-requests';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useFileDownload } from '@/composables/useFileDownload';
import { useTableFilter } from '@/composables/useTableFilter';
import { useGroupFilterVisible } from '@/composables/useGroupFilterVisible';
import { useGroupFilterStore } from '@/stores/group-filter';
import { requestErrorMessage } from '@/utils/api-errors';
import FilterMultiSelect from '@/components/filter-multi-select.vue';
import GroupFilterSelect from '@/components/group-filter-select.vue';

// Resolved (and, on demand, pending) requests of a request-date range, with the same
// filters as the xlsx export. The server bounds the range to MAX_RANGE_DAYS because the
// list is unpaginated.
const MAX_RANGE_DAYS = 93;
const MS_PER_DAY = 86_400_000;

const { t } = useI18n();
const { downloadFile } = useFileDownload();

const DATE_PART_LENGTH = 2;

function isoDate(date: Date): string {
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
    .map(part => String(part).padStart(DATE_PART_LENGTH, '0')).join('-');
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / MS_PER_DAY);
}

const today = new Date();
const createdFrom = ref(isoDate(new Date(today.getFullYear(), today.getMonth(), 1)));
const createdTo = ref(isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)));

const STATES: ShiftChangeRequest['aasmState'][] = ['pending', 'approved', 'rejected', 'expired', 'invalidated'];
const STATE_LABELS: Record<ShiftChangeRequest['aasmState'], string> = {
  pending: 'Pendiente',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  expired: 'Expirada',
  invalidated: 'Anulada',
};
const STATE_COLORS: Record<ShiftChangeRequest['aasmState'], string> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  expired: 'grey',
  invalidated: 'grey',
};
const REASON_CODES: ShiftChangeReasonCode[] = [
  'representative_request', 'caregiver_personal', 'caregiver_health', 'route_change', 'incident', 'other',
];

const stateOptions = STATES.map(state => ({ title: STATE_LABELS[state], value: state }));
const reasonOptions = [
  ...REASON_CODES.map(code => ({ title: t(`shiftChangeReasons.codes.${camelize(code)}`), value: code })),
  { title: t('shiftChangeReasons.withoutReason'), value: 'without_reason' },
];
const requestTypeOptions = [
  { title: 'Cambio de horario', value: 'time_change' },
  { title: 'Cambio de profesional', value: 'caregiver_change' },
];

// Pending requests already have their own tab.
const DEFAULT_STATES = STATES.filter(state => state !== 'pending');
const stateIn = ref<ShiftChangeRequest['aasmState'][]>([...DEFAULT_STATES]);
const reasonCodeIn = ref<NonNullable<ShiftChangeRequestFilters['reasonCodeIn']>>([]);
const requestTypeIn = ref<ShiftChangeRequest['requestType'][]>([]);

const { groupIds } = storeToRefs(useGroupFilterStore());
const { accessibleCareers } = useAccessibleCareers();
const careerIn = ref<string[]>([]);
const careerOptions = computed(() => [
  ...accessibleCareers.value,
  { title: 'Sin especialidad', value: 'without_career' },
]);

// The secondary filters live in a collapsible panel; whatever differs from its default
// stays visible as a removable chip, so nothing applied is ever hidden.
const filtersOpen = ref(false);
const { groups, shouldShow: groupFilterShown } = useGroupFilterVisible();

type Option = { title: string; value: string };
type ActiveFilter = { key: string; label: string; clear: () => void };

// One chip per filter that differs from its default: "Motivo: Incidente +2".
type ChipSource = {
  key: string;
  name: string;
  values: (string | number)[];
  options: Option[];
  clear: () => void;
};

function chip({ key, name, values, options, clear }: ChipSource): ActiveFilter | null {
  if (values.length === 0) return null;

  const [first, ...rest] = values.map(value => (
    options.find(option => option.value === String(value))?.title ?? String(value)));

  return { key, label: `${name}: ${first}${rest.length > 0 ? ` +${rest.length}` : ''}`, clear };
}

const statesChanged = computed(() => (
  stateIn.value.length !== DEFAULT_STATES.length ||
  DEFAULT_STATES.some(state => !stateIn.value.includes(state))));
const groupOptions = computed<Option[]>(() => (
  (groups.value ?? []).map(group => ({ title: group.name, value: String(group.id) }))));

function resetStates() {
  stateIn.value = [...DEFAULT_STATES];
}

// An emptied state selection means every state, which the chip spells out.
const stateChip = computed<ActiveFilter | null>(() => {
  if (!statesChanged.value) return null;

  return chip({ key: 'state', name: 'Estado', values: stateIn.value, options: stateOptions, clear: resetStates }) ??
    { key: 'state', label: 'Estado: todos', clear: resetStates };
});

const activeFilters = computed<ActiveFilter[]>(() => [
  stateChip.value,
  chip({
    key: 'reason', name: 'Motivo', values: reasonCodeIn.value, options: reasonOptions,
    clear: () => { reasonCodeIn.value = []; },
  }),
  chip({
    key: 'type', name: 'Tipo', values: requestTypeIn.value, options: requestTypeOptions,
    clear: () => { requestTypeIn.value = []; },
  }),
  chip({
    key: 'career', name: 'Especialidad', values: careerIn.value, options: careerOptions.value,
    clear: () => { careerIn.value = []; },
  }),
  chip({
    key: 'group', name: 'Grupo', values: groupIds.value, options: groupOptions.value,
    clear: () => { groupIds.value = []; },
  }),
].filter((filter): filter is ActiveFilter => filter !== null));

function clearFilters() {
  activeFilters.value.forEach(filter => filter.clear());
}

const rangeError = computed(() => {
  if (!createdFrom.value || !createdTo.value) return 'Selecciona ambas fechas.';

  const days = daysBetween(createdFrom.value, createdTo.value);
  if (days < 0) return 'La fecha inicial debe ser menor o igual a la fecha final.';
  if (days > MAX_RANGE_DAYS) return `El rango no puede superar los ${MAX_RANGE_DAYS} días.`;

  return '';
});

// An empty state selection means every state, sent explicitly so the query never
// degrades into the pending-only list.
const filters = computed<ShiftChangeRequestFilters>(() => ({
  aasmStateIn: stateIn.value.length > 0 ? stateIn.value : STATES,
  reasonCodeIn: reasonCodeIn.value,
  requestTypeIn: requestTypeIn.value,
  createdFrom: createdFrom.value,
  createdTo: createdTo.value,
  groupIdIn: groupIds.value,
  caregiverCareerIn: careerIn.value,
}));

// Deliberately outside `['shiftChangeRequests', 'pending']`: the shifts page badge
// must not refetch on every history filter change.
const { data: requests, isFetching: loading, error } = useQuery({
  queryKey: ['shiftChangeRequests', 'history', filters],
  queryFn: () => shiftChangeRequestsApi.index(filters.value),
  enabled: computed(() => !rangeError.value),
});
const errorText = computed(() => (error.value ?
  requestErrorMessage(error.value, 'No se pudo cargar el historial de solicitudes.') :
  ''));

const search = ref<string | null>('');
const matchesSearch = useTableFilter([
  'patientFullName', 'caregiverFullName', 'requestedCaregiverFullName', 'humanReason', 'reason',
]);
const visibleRequests = computed(() => (
  (requests.value ?? []).filter(request => matchesSearch('', search.value ?? '', request))
));

const headers = [
  { title: 'Solicitado', key: 'createdAt', sortable: true },
  { title: 'Turno', key: 'currentStartDatetime', sortable: true },
  { title: 'Paciente', key: 'patientFullName', sortable: false },
  { title: 'Profesional', key: 'caregiverFullName', sortable: false },
  { title: 'Tipo', key: 'humanRequestType', sortable: false },
  { title: 'Motivo', key: 'humanReason', sortable: false },
  { title: 'Estado', key: 'aasmState', sortable: false },
  { title: '', key: 'data-table-expand' },
];

// Cells print the human strings; sorting compares the raw ISO timestamps as instants
// (offsets differ across DST, so plain string order is not enough).
function byInstant(a: string, b: string): number {
  return Date.parse(a) - Date.parse(b);
}
const sortRaw = { createdAt: byInstant, currentStartDatetime: byInstant };

function proposedValue(request: ShiftChangeRequest): string {
  return (request.requestType === 'caregiver_change' ?
    request.requestedCaregiverFullName :
    request.humanRequestedTimes) ?? '—';
}

const downloading = ref(false);
const downloadError = ref('');

async function download() {
  downloading.value = true;
  downloadError.value = '';
  try {
    downloadFile(await shiftChangeRequestsApi.download(filters.value));
  } catch (downloadFailure) {
    downloadError.value = requestErrorMessage(downloadFailure, 'No se pudo descargar el Excel.');
  } finally {
    downloading.value = false;
  }
}
</script>

<template>
  <div>
    <v-alert
      v-if="errorText || downloadError"
      type="error"
      variant="tonal"
      density="compact"
      class="mb-3"
    >
      {{ errorText || downloadError }}
    </v-alert>

    <div class="d-flex flex-wrap align-start ga-3 pt-2 mb-2">
      <div class="d-flex ga-2 date-range">
        <v-text-field
          v-model="createdFrom"
          data-testid="history-created-from"
          type="date"
          label="Solicitado desde"
          variant="outlined"
          density="compact"
          hide-details
          :error="!!rangeError"
        />
        <v-text-field
          v-model="createdTo"
          data-testid="history-created-to"
          type="date"
          label="Hasta"
          variant="outlined"
          density="compact"
          hide-details
          :error="!!rangeError"
        />
      </div>
      <v-text-field
        v-model="search"
        class="search-field"
        label="Buscar"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        single-line
        hide-details
        clearable
      />
      <v-btn
        data-testid="history-filters-toggle"
        variant="outlined"
        height="40"
        :append-icon="filtersOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        @click="filtersOpen = !filtersOpen"
      >
        <v-icon
          icon="mdi-filter-variant"
          start
        />
        Filtros
        <v-badge
          v-if="activeFilters.length > 0"
          data-testid="history-filters-count"
          :content="activeFilters.length"
          color="primary"
          inline
        />
      </v-btn>
      <v-spacer />
      <v-btn
        data-testid="history-download"
        prepend-icon="mdi-microsoft-excel"
        variant="tonal"
        color="primary"
        height="40"
        :loading="downloading"
        :disabled="!!rangeError"
        @click="download"
      >
        <span class="d-none d-sm-inline">Descargar&nbsp;</span>Excel
      </v-btn>
    </div>
    <div
      v-if="rangeError"
      class="text-caption text-error mb-2"
    >
      {{ rangeError }}
    </div>

    <v-expand-transition>
      <v-sheet
        v-show="filtersOpen"
        data-testid="history-filters-panel"
        border
        rounded
        class="px-4 pt-2 pb-4 mb-3"
      >
        <v-row dense>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-caption text-medium-emphasis mb-1">
              Estado
            </div>
            <filter-multi-select
              v-model="stateIn"
              data-testid="history-state"
              :items="stateOptions"
              label="Estado"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-caption text-medium-emphasis mb-1">
              Motivo
            </div>
            <filter-multi-select
              v-model="reasonCodeIn"
              data-testid="history-reason"
              :items="reasonOptions"
              label="Motivo"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-caption text-medium-emphasis mb-1">
              Tipo
            </div>
            <filter-multi-select
              v-model="requestTypeIn"
              data-testid="history-request-type"
              :items="requestTypeOptions"
              label="Tipo"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div class="text-caption text-medium-emphasis mb-1">
              Especialidades
            </div>
            <filter-multi-select
              v-model="careerIn"
              data-testid="history-career"
              :items="careerOptions"
              label="Especialidades"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <div
              v-if="groupFilterShown"
              class="text-caption text-medium-emphasis"
            >
              Grupos
            </div>
            <group-filter-select
              v-model="groupIds"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>
      </v-sheet>
    </v-expand-transition>

    <div
      v-if="activeFilters.length > 0"
      data-testid="history-active-filters"
      class="d-flex flex-wrap align-center ga-2 mb-3"
    >
      <v-chip
        v-for="filter in activeFilters"
        :key="filter.key"
        size="small"
        closable
        @click:close="filter.clear()"
      >
        {{ filter.label }}
      </v-chip>
      <v-btn
        data-testid="history-clear-filters"
        size="small"
        variant="text"
        color="primary"
        @click="clearFilters"
      >
        Limpiar filtros
      </v-btn>
    </div>

    <v-data-table
      :headers="headers"
      :items="visibleRequests"
      :loading="loading"
      :custom-key-sort="sortRaw"
      :sort-by="[{ key: 'createdAt', order: 'desc' }]"
      item-value="id"
      show-expand
      density="compact"
      no-data-text="No hay solicitudes para los filtros seleccionados."
      items-per-page-text="Solicitudes por página"
    >
      <template #[`item.createdAt`]="{ item }">
        {{ item.humanCreatedAt }}
      </template>

      <template #[`item.currentStartDatetime`]="{ item }">
        {{ item.humanShiftDate }}
      </template>

      <template #[`item.humanReason`]="{ item }">
        {{ item.humanReason ?? t('shiftChangeReasons.withoutReason') }}
      </template>

      <template #[`item.aasmState`]="{ item }">
        <v-chip
          size="small"
          :color="STATE_COLORS[item.aasmState]"
        >
          {{ item.humanAasmState }}
        </v-chip>
      </template>

      <template #expanded-row="{ columns, item }">
        <tr>
          <td
            :colspan="columns.length"
            class="py-3 text-body-2"
          >
            <div>
              {{ item.requestType === 'caregiver_change' ? 'Profesional actual' : 'Horario actual' }}:
              {{ item.requestType === 'caregiver_change' ? item.caregiverFullName : item.humanCurrentTimes }}
              →
              {{ item.requestType === 'caregiver_change' ? 'Reemplazo propuesto' : 'Horario propuesto' }}:
              <span class="font-weight-medium">{{ proposedValue(item) }}</span>
            </div>
            <div v-if="item.reason">
              {{ item.humanReason ? 'Comentario' : 'Motivo' }}:
              <span class="font-italic">“{{ item.reason }}”</span>
            </div>
            <div v-if="item.processedByEmail">
              Procesada por: {{ item.processedByEmail }}
            </div>
            <div v-if="item.humanProcessedAt">
              Procesada el: {{ item.humanProcessedAt }}
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>
.date-range {
  flex: 1 1 320px;
  max-width: 360px;
}

.search-field {
  flex: 1 1 200px;
  max-width: 280px;
}

/* Phones: dates and search take the whole row, Filtros and Excel share the next. */
@media (max-width: 599px) {
  .date-range,
  .search-field {
    flex-basis: 100%;
    max-width: none;
  }
}
</style>
